/* eslint-disable */
import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, Home } from "lucide-react";

function formatEUR(n) {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);
}

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState({ state: "loading", data: null });
  const { clear } = useCart();
  const cancelledRef = useRef(false);
  const clearRef = useRef(clear);
  clearRef.current = clear;

  useEffect(() => {
    cancelledRef.current = false;
    if (!sessionId) {
      setStatus({ state: "error", data: { message: "Aucune session de paiement" } });
      return;
    }

    let attempt = 0;
    const tick = async () => {
      if (cancelledRef.current) return;
      if (attempt > 12) {
        setStatus({ state: "timeout", data: null });
        return;
      }
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelledRef.current) return;
        if (data.payment_status === "paid") {
          setStatus({ state: "success", data });
          clearRef.current?.();
          trackEvent("Purchase", {
            value: (data.amount_total || 0) / 100,
            currency: data.currency?.toUpperCase() || "EUR",
            content_name: data.metadata?.cart,
          });
          return;
        }
        if (data.status === "expired") {
          setStatus({ state: "expired", data });
          return;
        }
        attempt += 1;
        setTimeout(tick, 2000);
      } catch {
        attempt += 1;
        setTimeout(tick, 2500);
      }
    };
    tick();

    return () => {
      cancelledRef.current = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#FDFBF9] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center bg-white border border-[#EADBD1] rounded-3xl p-10 shadow-sm" data-testid="checkout-success-card">
        {status.state === "loading" && (
          <>
            <Loader2 className="h-12 w-12 text-[#C58C85] mx-auto animate-spin" />
            <h1 className="font-serif text-3xl mt-6 text-[#2C2420]">Vérification du paiement…</h1>
            <p className="text-[#6B5D54] mt-3">Merci de patienter quelques secondes.</p>
          </>
        )}
        {status.state === "success" && (
          <>
            <CheckCircle2 className="h-14 w-14 text-[#25D366] mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl mt-6 text-[#2C2420]">Paiement confirmé !</h1>
            <p className="text-[#6B5D54] mt-3">
              Merci pour votre commande. Vous recevrez un email de confirmation et votre commande sera expédiée sous 24h ouvrées.
            </p>
            {status.data?.metadata?.cart && (
              <div className="mt-6 rounded-2xl bg-[#F5EFE6] p-4 text-left">
                <p className="text-xs uppercase tracking-[0.22em] text-[#C58C85] font-medium">Récapitulatif</p>
                <p className="text-sm text-[#2C2420] mt-2">{status.data.metadata.cart}</p>
                <p className="text-sm font-medium text-[#2C2420] mt-2">
                  Total : {formatEUR((status.data.amount_total || 0) / 100)}
                </p>
              </div>
            )}
            <Link to="/">
              <Button className="mt-8 rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white px-7 h-12" data-testid="success-home-btn">
                <Home className="h-4 w-4 mr-2" /> Retour à l&apos;accueil
              </Button>
            </Link>
          </>
        )}
        {(status.state === "expired" || status.state === "error" || status.state === "timeout") && (
          <>
            <XCircle className="h-14 w-14 text-red-500 mx-auto" strokeWidth={1.5} />
            <h1 className="font-serif text-3xl mt-6 text-[#2C2420]">Paiement non confirmé</h1>
            <p className="text-[#6B5D54] mt-3">
              {status.state === "expired"
                ? "La session de paiement a expiré. Réessayez."
                : "Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, contactez-nous via WhatsApp."}
            </p>
            <Link to="/">
              <Button className="mt-8 rounded-full bg-[#2C2420] hover:bg-[#3a2f2a] text-white px-7 h-12" data-testid="success-home-btn-2">
                <Home className="h-4 w-4 mr-2" /> Retour à l&apos;accueil
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
